import { $LocalStorage } from '../utils/StorageUtils'

/**
 * Bidirectional dark-mode sync between the new-UI (Fusion) host and this CCS instance when CCS is
 * embedded as an iframe (portal admin pages or the legacy view).
 *
 * Host -> CCS: the host posts {@link FUSION_THEME_MESSAGE_TYPE} when the user toggles the theme in
 * the new UI; {@link applyDarkMode} reflects it here even on portal pages where the CCS header (and
 * therefore {@code useDarkMode}) is not mounted.
 *
 * CCS -> host: {@link sendThemeToParent} is called by {@code useDarkMode} when the user toggles dark
 * mode from the legacy user menu, so the new UI follows the legacy view's theme.
 *
 * Keep the message type in sync with the host contract in akixi-frontend:
 *   packages/ui/src/features/legacy-mode/model/ccsMessages.ts
 */
export const FUSION_THEME_MESSAGE_TYPE = 'akixi:fusion:theme'

// Mirror of the constants owned by React/Common/Hooks/useDarkMode.tsx. Duplicated here so this
// NonReact bootstrap module stays free of React imports; keep the values identical.
const DARK_MODE_CLASS = 'dark-mode'
const DARK_MODE_STORAGE = 'isDarkMode'

const FUSION_HOST_ORIGIN_STORAGE_KEY = 'akixiFusionHostOrigin'

/**
 * Best-known origin of the Fusion host that embedded this CCS page. Seeded at module load from a
 * previously-persisted value or (same-origin) the parent/referrer, and filled in RELIABLY the first
 * time the host posts a message to this iframe — see the listener in {@link initFusionThemeBridge},
 * which records {@code event.origin}. Cross-origin dev/prod loads can't read the parent origin
 * directly and the seeded URL param is lost across CCS's internal redirects, so the captured value
 * is what makes CCS -> host postMessages (e.g. the Sign-In As reauth hand-off, CXUI-402) reliable.
 */
let dynamicHostOrigin: Nullable<string> = ((): Nullable<string> => {
    try {
        const stored: OnlyNullable<string> = window.sessionStorage.getItem(FUSION_HOST_ORIGIN_STORAGE_KEY)

        if (stored) {
            return stored
        }
    } catch {
        // sessionStorage unavailable — fall through.
    }

    try {
        if (window.parent !== window) {
            return window.parent.location.origin
        }
    } catch {
        if (document.referrer) {
            try {
                return new URL(document.referrer).origin
            } catch {
                // Referrer URL could not be parsed.
            }
        }
    }

    return null
})()

/**
 * Records the Fusion host origin learnt from a trusted message the host posted to this iframe, so
 * later CCS -> host postMessages target it correctly even across cross-origin in-iframe navigations.
 *
 * @param origin - The {@code event.origin} of a message received from the host.
 */
const rememberFusionHostOrigin = (origin: string): void => {
    if (!origin || origin === window.location.origin) return

    dynamicHostOrigin = origin

    try {
        window.sessionStorage.setItem(FUSION_HOST_ORIGIN_STORAGE_KEY, origin)
    } catch {
        // sessionStorage unavailable — keep the in-memory value.
    }
}

/**
 * Current best-known origin of the embedding Fusion host, or {@code null} when standalone/unknown.
 */
export const getFusionHostOrigin = (): Nullable<string> => dynamicHostOrigin

/**
 * Whether this CCS page is embedded as an iframe of the Fusion host.
 */
export const isFusionFramed = (): boolean => window.parent !== window

/**
 * Notifies the Fusion host of a dark-mode change made inside CCS so the new UI can follow it.
 * No-op when standalone or when the host origin is not yet known.
 *
 * @param dark - The new dark-mode state.
 */
export const sendThemeToParent = (dark: boolean): void => {
    const hostOrigin: Nullable<string> = getFusionHostOrigin()

    if (!isFusionFramed() || !hostOrigin) return

    try {
        window.parent.postMessage({ type: FUSION_THEME_MESSAGE_TYPE, dark }, hostOrigin)
    } catch {
        // Best effort — the parent may be navigating or torn down.
    }
}

/**
 * Applies the given dark-mode state to this page. Toggling the body class covers portal pages where
 * {@code useDarkMode} is not mounted; writing the persisted key announces via {@code localStorageSet$}
 * so a mounted {@code useDarkMode} (legacy view) syncs its switch and state too.
 *
 * @param dark - Desired dark-mode state.
 */
const applyDarkMode = (dark: boolean): void => {
    document.body?.classList.toggle(DARK_MODE_CLASS, dark)
    $LocalStorage.setItem(DARK_MODE_STORAGE, dark)
}

/**
 * Reads the initial theme seeded by the host on the iframe URL ({@code ?fusionTheme=dark|light}) so
 * the first paint matches the host, ahead of the postMessage that follows on load.
 *
 * @return {@code true}/{@code false} for the seeded dark state, or {@code null} when absent.
 */
const readInitialFusionTheme = (): OnlyNullable<boolean> => {
    const value: OnlyNullable<string> = new URLSearchParams(window.location.search).get('fusionTheme')

    if (value === 'dark') return true

    if (value === 'light') return false

    return null
}

let initialised = false

/**
 * Initialises the host -> CCS half of the theme bridge: applies the URL-seeded theme and starts
 * listening for theme messages from the host. Safe to call more than once and a no-op when this
 * page is not embedded in the Fusion host. Call once at CCS page bootstrap.
 */
export const initFusionThemeBridge = (): void => {
    if (initialised || !isFusionFramed()) return

    initialised = true

    const initialDark: OnlyNullable<boolean> = readInitialFusionTheme()

    if (initialDark !== null) {
        applyDarkMode(initialDark)
    }

    window.addEventListener('message', (event: MessageEvent): void => {
        // Only trust messages from our actual embedding parent (the host). CCS framing is locked
        // down to the new UI, so window.parent is the host; this guard stops any other window from
        // spoofing the host origin we later send the session token to (CXUI-402).
        if (event.source !== window.parent) return

        const knownOrigin: Nullable<string> = getFusionHostOrigin()

        if (knownOrigin && event.origin !== knownOrigin) return

        const data = event.data as Nullable<{ type?: unknown; dark?: unknown }>
        const dark: unknown = data?.dark

        if (data?.type !== FUSION_THEME_MESSAGE_TYPE || typeof dark !== 'boolean') return

        // The host posts theme messages to this iframe on load; capture its exact origin so later
        // CCS -> host postMessages (the Sign-In As reauth hand-off) target it reliably (CXUI-402).
        rememberFusionHostOrigin(event.origin)

        applyDarkMode(dark)
    })
}
